import React, { memo, useMemo } from "react";
import { dateFormat } from "../../../utils/helper";
import moment from "moment";
import { NoDataFound } from "./tableComponent";
import { LuMessageSquareText } from "react-icons/lu";
import { Tooltip } from "flowbite-react";
import { IconActionButton } from "../action/iconActionButton";

// CSS utility class for flexbox centering
const flexCenter = "flex items-center justify-center";

// Helper: format date into Today / Yesterday / Weekday / default format
const formatDisplayDate = (isoDate) => {
  if (!isoDate) return "-";

  const today = moment().format("YYYY-MM-DD");
  const yesterday = moment().subtract(1, "days").format("YYYY-MM-DD");
  const twoDaysAgo = moment().subtract(2, "days").format("YYYY-MM-DD");

  if (isoDate === today) return "Today";
  if (isoDate === yesterday) return "Yesterday";
  if (isoDate === twoDaysAgo) return moment().subtract(2, "days").format("dddd");

  return dateFormat(isoDate);
};

// Helper: derive high-level status for a row based on data + flags (weekend/holiday/leave)
const deriveRowStatus = (entry) => {
  // Give priority to explicit flags from API
  if (entry?.is_holiday) return "Holiday";
  if (entry?.is_leave) return "Leave";
  if (entry?.is_weekend) return "Weekend";

  const start_time = entry?.start_time;
  
  // If there is any attendance (start_time available), treat as Present for summary
  if (start_time && start_time !== "-") {
    return "Present";
  }

  // Otherwise treat as Absent
  return "Absent";
};

// Loading skeleton
const AttendanceTableLoading = memo(({ colCount }) => {
  return Array(3)
    .fill(null)
    .map((_, idx) => (
      <div key={idx} className="flex gap-2 pt-1">
        {Array(colCount)
          .fill(null)
          .map((__, colIdx) => (
            <div
              key={colIdx}
              className="flex-1 animate-pulse bg-light h-8 rounded-sm"
            />
          ))}
      </div>
    ));
});

// Connector component
const Connector = memo(({ status, start_time, end_time, totalHours }) => {
  // Check if staff is present (has both start and end times)
  const isPresent = start_time && start_time !== "-" && end_time && end_time !== "-";
  
  // Check if partial (has start time but no end time)
  const isPartial = start_time && start_time !== "-" && (!end_time || end_time === "-");
  
  // Calculate time difference for line width
  const getTimeDifference = () => {
    if (!isPresent) return 0;
    
    try {
      const start = moment(start_time, "HH:mm:ss");
      const end = moment(end_time, "HH:mm:ss");
      const diffHours = end.diff(start, 'hours', true);
      return Math.max(0.5, Math.min(8, diffHours)); // Clamp between 0.5 and 8 hours
    } catch (error) {
      return 4; // Default to 4 hours if parsing fails
    }
  };

  const timeDiff = getTimeDifference();
  
  // Calculate line widths
  const workedWidth = isPresent ? `${(timeDiff / 8) * 100}%` : isPartial ? "50%" : "0%";
  const remainingWidth = isPresent ? `${((8 - timeDiff) / 8) * 100}%` : isPartial ? "50%" : "100%";
  
  // Determine colors
  const workedColor = isPresent ? "bg-green-500" : isPartial ? "bg-yellow-500" : "bg-red-500";
  const remainingColor = isPresent ? "bg-red-500" : isPartial ? "bg-yellow-500" : "bg-red-500";
  const dotColor = isPresent ? "bg-green-500" : isPartial ? "bg-yellow-500" : "bg-red-500";
  
  const minWidth = "min-w-[80px]";

  // Get status text and color
  const getStatusInfo = () => {
    if (isPresent) {
      if (timeDiff >= 8) return { text: "Full Day", color: "text-green-600" };
      if (timeDiff >= 4) return { text: "Half Day", color: "text-yellow-600" };
      return { text: "Short Day", color: "text-orange-600" };
    }
    if (isPartial) {
      return { text: "Partial", color: "text-yellow-600" };
    }
    return { text: "Absent", color: "text-red-600" };
  };

  const statusInfo = getStatusInfo();

  return (
    <div className={`flex flex-col items-center w-full ${minWidth}`}>
      {/* Status indicator */}
      <div className={`text-xs font-medium mb-2 ${statusInfo.color}`}>
        {statusInfo.text}
      </div>
      
      {/* Connector line */}
      <div className="flex items-center w-full">
        {/* Left dot (start time) */}
        <span className={`h-4 rounded-full flexCenter text-white ${dotColor} w-4 flex-shrink-0`}>
          {isPresent && (
            <Tooltip content={`Start: ${start_time}`} placement="top">
              <span className="text-xs">S</span>
            </Tooltip>
          )}
        </span>

        {/* Mixed line: Green (worked) + Red (remaining) */}
        <div className="flex-1 mx-2 relative">
          <div className="flex w-full">
            {/* Green part - worked time */}
            <div 
              className={`h-[3px] ${workedColor} transition-all duration-300 rounded-l-full`}
              style={{ width: workedWidth }}
            />
            {/* Red part - remaining time */}
            <div 
              className={`h-[3px] ${remainingColor} transition-all duration-300 rounded-r-full`}
              style={{ width: remainingWidth }}
            />
          </div>
          
          {/* Time labels */}
          {/* {isPresent && (
            <div className="absolute top-0 left-0 right-0 flex justify-between text-xs text-gray-500 mt-2">
              <span className="font-medium">{start_time}</span>
              <span className="font-medium">{end_time}</span>
            </div>
          )} */}
        </div>

        {/* Right dot (end time) */}
        <span className={`h-4 rounded-full flexCenter text-white ${dotColor} w-4 flex-shrink-0`}>
          {isPresent && (
            <Tooltip content={`End: ${end_time}`} placement="top">
              <span className="text-xs">E</span>
            </Tooltip>
          )}
        </span>
      </div>
      
      {/* Time breakdown display */}
      {/* {isPresent && (
        <div className="text-xs text-gray-600 mt-1 font-medium">
          <span className="text-green-600">Worked: {totalHours}</span>
          {timeDiff < 8 && (
            <span className="text-red-600 ml-2">
              Remaining: {Math.round((8 - timeDiff) * 60)}m
            </span>
          )}
        </div>
      )} */}
    </div>
  );
});

const TableHeader = memo(({ headers, showActions }) => {
  return (
    <div className="flex bg-light p-4 font-bold">
      {headers.map((header, index) => {
        if (header.connecter?.length) {
          return (
            <div key={index} className="flex justify-center flex-row gap-4 items-center min-w-80 flex-1">
              <span className="min-w-[60px] text-center text-sm">Start</span>
              <span className="min-w-[80px] text-center text-sm"></span>
              <span className="min-w-[60px] text-center text-sm">End</span>
            </div>
          );
        }

        return (
          <div
            key={index}
            className={`flex-1 min-w-[150px] ${header?.last ? "text-right" : ""}`}
          >
            {header?.name || "-"}
          </div>
        );
      })}
      
      {/* Action column header */}
      {showActions && (
        <div className="min-w-[100px] text-center">Actions</div>
      )}
    </div>
  );
});

const AttendanceTable = ({ 
  attendanceData = [], 
  getStatusStyles, 
  headers = [], 
  loading = true,
  showActions = true,
  action = {},
}) => {
 
  // Memoize computed rows to avoid recalculations on re-render
  const computedRows = useMemo(() => {
    return (attendanceData || []).map((entry) => {
      const status = deriveRowStatus(entry);
      const colorKey = status === "Running" ? "Present" : status;
      const { bgColor } = getStatusStyles ? getStatusStyles(colorKey) : { bgColor: "bg-gray-400" };
      const staffName = entry?.staffName;
      const start_time = entry?.start_time;
      const end_time = entry?.end_time;
      const totalHours = entry?.totalHours;
      
      return { 
        entry, 
        status, 
        bgColor, 
        staffName, 
        start_time, 
        end_time, 
        totalHours 
      };
    });
  }, [attendanceData, getStatusStyles]);

  // Generate action buttons for each row
  const generateActionButtons = (item) => {
    const actionButtons = [];

    const actionConfigs = [
      { key: "view", tooltip: "View" },
      { key: "edit", tooltip: "Edit" },
      { key: "download", tooltip: "Download" },
      { key: "delete", tooltip: "Delete" },
    ];

    actionConfigs.forEach(({ key, tooltip }) => {
      if (action?.[key]) {
        actionButtons.push({
          name: key,
          tooltip,
          onClick: () => action[key](item),
        });
      }
    });

    return actionButtons;
  };

  if (loading) {
    return (
      <div className="overflow-x-auto flex flex-col gap-1 text-theme">
        <TableHeader headers={headers} showActions={showActions} />
        <AttendanceTableLoading colCount={headers.length + (showActions ? 1 : 0)} />
      </div>
    );
  }

  if (!computedRows.length) {
    return (
      <div className="overflow-x-auto flex flex-col gap-1 text-theme">
        <TableHeader headers={headers} showActions={showActions} />
        <NoDataFound asTableCell={false} />
      </div>
    );
  }

  return (
    <div className="overflow-x-auto flex flex-col gap-1 text-theme">
      <TableHeader headers={headers} showActions={showActions} />

      {computedRows.map(({ entry, status, bgColor, staffName, start_time, end_time, totalHours }, rowIdx) => (
        <div key={rowIdx} className="flex items-center p-4 bg-white border-b group hover:bg-gray-50 transition-colors duration-200">
          {headers.map((header, colIdx) => {
            // Special connecter column: In / Connector / Out
           
            if (header.connecter?.length) {
              return (
                <div
                  key={colIdx}
                  className="flex justify-center flex-row gap-4 items-center min-w-80 flex-1"
                >
                  <span className="min-w-[60px] text-center text-sm text-gray-500">
                    {start_time !== "-" ? start_time : "—"}
                  </span>

                  <Connector 
                    status={status} 
                    start_time={start_time} 
                    end_time={end_time} 
                    totalHours={totalHours}
                  />

                  <span className="min-w-[60px] text-center text-sm text-gray-500">
                    {end_time !== "-" ? end_time : "—"}
                  </span>
                </div>
              );
            }

            const key = header?.key;
            let value = entry?.[key];

            if (key === "date") {
              value = formatDisplayDate(entry?.date);
            }

            if (key === "status") {
              value = status;
            }
            if(key === 'staffName'){
             
              value = staffName?.name
            }

            if(key === 'start_time'){
              value = start_time
            }

            if(key === 'end_time'){
              value = end_time
            }

            return (
              <div key={colIdx} className={`flex-1 ${header?.last ? "text-right" : ""}`}>
                {value ?? "-"}
              </div>
            );
          })}
          
          {/* Action buttons column - hidden by default, visible on hover */}
          {showActions && (
            <div className="min-w-[100px] flex justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
              <IconActionButton actions={generateActionButtons(entry)} />
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default memo(AttendanceTable);
